import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown, X } from "lucide-react";

// Premium searchable company selector — replaces the plain native <select>
// with an institutional research-tool feel (identity card + search panel).
export const CompanySelector = ({ testId, label, value, onChange, universe, exclude, accent, onOpenChange }) => {
  const [open, setOpenState] = useState(false);
  const setOpen = React.useCallback((next) => {
    const resolved = typeof next === "function" ? next(open) : next;
    setOpenState(resolved);
    onOpenChange?.(resolved);
  }, [onOpenChange, open]);
  const [q, setQ] = useState("");
  const selected = universe.find((c) => c.ticker === value);
  const tone = accent === "amber" ? "amber" : "sky";
  const labelId = `${testId}-label`;
  const listboxId = `${testId}-listbox`;

  const filtered = useMemo(() => {
    const list = universe.filter((c) => c.ticker !== exclude);
    if (!q.trim()) return list;
    const s = q.trim().toLowerCase();
    return list.filter((c) => c.ticker.toLowerCase().includes(s) || c.name.toLowerCase().includes(s));
  }, [universe, exclude, q]);

  const pick = (ticker) => { onChange(ticker); setOpen(false); setQ(""); };

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  return (
    <div className="relative">
      <div id={labelId} className={`kicker mb-1.5 ${tone === "amber" ? "text-amber-500/70" : "text-sky-400/70"}`}>{label}</div>
      <div
        role="button"
        tabIndex={0}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelId}
        aria-controls={listboxId}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        className={`lv-press flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border bg-[#0B0C0E] px-4 py-3 text-left transition-colors ${
          tone === "amber" ? "border-amber-500/25 hover:border-amber-500/45" : "border-sky-500/25 hover:border-sky-500/45"
        }`}
      >
        <AnimatePresence mode="wait">
          {selected ? (
            <motion.div key={selected.ticker} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.18 }} className="min-w-0">
              <div className={`font-mono text-sm font-bold ${tone === "amber" ? "text-amber-300" : "text-sky-300"}`}>{selected.ticker}</div>
              <div className="truncate text-sm text-white">{selected.name}</div>
            </motion.div>
          ) : (
            <span className="text-sm text-zinc-500">Select company…</span>
          )}
        </AnimatePresence>
        <div className="flex shrink-0 items-center gap-1">
          {selected && (
            <button
              type="button"
              aria-label={`Clear ${label} selection`}
              data-testid={`${testId}-clear`}
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="rounded p-1 text-zinc-600 transition-colors hover:text-zinc-300"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <ChevronDown aria-hidden="true" className={`h-4 w-4 text-zinc-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <button type="button" tabIndex={-1} aria-label={`Close ${label} options`} className="fixed inset-0 z-40 cursor-default" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-lg border border-white/12 bg-[#101215] shadow-2xl"
              role="listbox"
              id={listboxId}
              aria-labelledby={labelId}
            >
              <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2.5">
                <Search className="h-3.5 w-3.5 text-zinc-500" aria-hidden="true" />
                <input
                  autoFocus
                  data-testid={`${testId}-search`}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search ticker or company name…"
                  aria-label={`Search ${label}`}
                  className="w-full bg-transparent text-sm text-white outline-none placeholder:text-zinc-500"
                />
              </div>
              <div className="max-h-64 overflow-y-auto p-1.5">
                {filtered.length === 0 && <p className="px-3 py-6 text-center text-xs text-zinc-500">No companies match “{q}”.</p>}
                {filtered.map((c) => (
                  <button
                    key={c.ticker}
                    type="button"
                    role="option"
                    aria-selected={c.ticker === value}
                    data-testid={`${testId}-option-${c.ticker.toLowerCase()}`}
                    onClick={() => pick(c.ticker)}
                    className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-white/[0.06]"
                  >
                    <span className="min-w-0 truncate">
                      <span className="mr-2 font-mono text-xs font-semibold text-zinc-300">{c.ticker}</span>
                      <span className="text-xs text-zinc-500">{c.name}</span>
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
